import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { brmAPI } from "@/services/api";
import { setCachedWallet, getCachedWallet } from "@/services/offline";
import { 
  Wallet, 
  ArrowUpRight, 
  ArrowDownLeft, 
  RefreshCw, 
  TrendingUp,
  Loader2,
} from "lucide-react";
import {
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";

const BRMWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  
  // Withdrawal form
  const [amount, setAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletRes, txRes] = await Promise.all([
        brmAPI.getWallet(),
        brmAPI.getTransactions()
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data);
      setCachedWallet(walletRes.data);
    } catch (error) {
      const cached = getCachedWallet();
      if (cached) {
        setWallet(cached.data);
        toast.info("Showing cached data");
      } else {
        toast.error("Failed to load wallet");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) < 1000) {
      toast.error("Minimum withdrawal is ₦1,000");
      return;
    }
    if (!bankName || !accountNumber || !accountName) {
      toast.error("Please fill all bank details");
      return;
    }

    setWithdrawing(true);
    try {
      const res = await brmAPI.requestWithdrawal(
        parseFloat(amount),
        bankName,
        accountNumber,
        accountName
      );
      toast.success("Withdrawal requested!", {
        description: `Reference: ${res.data.reference}`
      });
      setShowWithdraw(false);
      setAmount("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    } finally {
      setWithdrawing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900" data-testid="wallet-title">
            Wallet
          </h1>
          <p className="text-sm text-gray-500">
            Manage your earnings
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          data-testid="refresh-wallet-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Balance Card */}
      <section className="overflow-hidden rounded-2xl border border-brand-200 bg-brand-500 p-6 text-white shadow-theme-xs">
          <div>
            <p className="text-sm font-medium text-white/80">Available Balance</p>
            {loading ? (
              <Skeleton className="h-12 w-40 bg-white/20 mt-2" />
            ) : (
              <p className="text-4xl font-semibold tracking-tight mt-1" data-testid="wallet-balance">
                {formatCurrency(wallet?.balance)}
              </p>
            )}
            <Button
              variant="secondary"
              className="mt-4"
              onClick={() => setShowWithdraw(true)}
              disabled={loading || !wallet || wallet.balance < 1000}
              data-testid="withdraw-btn"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Withdraw
            </Button>
          </div>
      </section>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-4">
        <TailAdminMetricCard
          label="Lifetime"
          value={formatCurrency(wallet?.lifetime_earnings)}
          icon={TrendingUp}
          tone="success"
          helper="All-time earnings"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Pending"
          value={wallet?.pending_withdrawals || 0}
          icon={Wallet}
          tone="warning"
          helper="Withdrawals"
          loading={loading}
        />
      </div>

      {/* Per-campaign earnings */}
      {!loading && wallet?.campaign_earnings && Object.keys(wallet.campaign_earnings).length > 0 && (
        <TailAdminPanel title="Earnings by campaign" description="Commission breakdown per Prove-Request campaign">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-4">Campaign</th>
                  <th className="pb-2 text-right">Earned</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(wallet.campaign_earnings)
                  .sort(([, a], [, b]) => b - a)
                  .map(([id, amount]) => (
                    <tr key={id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2.5 pr-4 font-mono text-xs text-gray-600">{id}</td>
                      <td className="py-2.5 text-right font-semibold text-emerald-600 tabular-nums">
                        {formatCurrency(amount)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TailAdminPanel>
      )}

      {/* Transactions */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Transaction History
        </h2>
        
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
              <section key={i} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24 mt-1" />
                  </div>
                  <Skeleton className="h-5 w-20" />
                </div>
              </section>
          ))
        ) : transactions.length === 0 ? (
          <TailAdminEmptyState
            icon={Wallet}
            title="No transactions yet"
            description="Your earnings and withdrawals will appear here."
          />
        ) : (
          transactions.map((tx) => (
            <section key={tx.id} data-testid={`transaction-${tx.id}`} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
                <div className="flex items-center gap-3">
                  <div className={`transaction-icon ${tx.type}`}>
                    {tx.type === "credit" ? (
                      <ArrowDownLeft className="w-5 h-5" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">
                      {tx.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(tx.created_at)} - {tx.reference}
                    </p>
                  </div>
                  <p className={`font-bold tabular-nums ${tx.type === "credit" ? "text-emerald-600" : "text-red-600"}`}>
                    {tx.type === "credit" ? "+" : "-"}{formatCurrency(tx.amount)}
                  </p>
                </div>
              </section>
          ))
        )}
      </div>

      {/* Withdrawal Dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Withdrawal</DialogTitle>
            <DialogDescription>
              Enter the amount and bank details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-sm font-medium text-gray-700">Amount (₦)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="10000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={1000}
                max={wallet?.balance || 0}
                className="border-gray-200 focus:border-brand-400"
                data-testid="withdraw-amount-input"
              />
              <p className="text-xs text-gray-500">
                Available: {formatCurrency(wallet?.balance)} - Min: ₦1,000
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank" className="text-sm font-medium text-gray-700">Bank Name</Label>
              <Input
                id="bank"
                placeholder="e.g. GTBank, Access Bank"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="border-gray-200 focus:border-brand-400"
                data-testid="withdraw-bank-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account" className="text-sm font-medium text-gray-700">Account Number</Label>
              <Input
                id="account"
                placeholder="0123456789"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                maxLength={10}
                className="border-gray-200 focus:border-brand-400"
                data-testid="withdraw-account-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">Account Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="border-gray-200 focus:border-brand-400"
                data-testid="withdraw-name-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdraw(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleWithdraw} 
              disabled={withdrawing}
              data-testid="confirm-withdraw-btn"
            >
              {withdrawing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Withdraw {amount ? formatCurrency(parseFloat(amount)) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BRMWallet;
